import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import api from '../api/axios';

const AdminUsuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [procesandoId, setProcesandoId] = useState(null);
  const [clavesTemporales, setClavesTemporales] = useState({});

  const [cuentaNombre, setCuentaNombre] = useState('');
  const [cuentaTipo, setCuentaTipo] = useState('proveedor');
  const [cuentaSapId, setCuentaSapId] = useState('');
  const [creandoCuenta, setCreandoCuenta] = useState(false);

  const [busquedaEmpresa, setBusquedaEmpresa] = useState('');
  const [resultadosEmpresa, setResultadosEmpresa] = useState([]);
  const [buscandoEmpresa, setBuscandoEmpresa] = useState(false);
  const [seleccionandoEmpresa, setSeleccionandoEmpresa] = useState(false);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);

  const [usuarioNombre, setUsuarioNombre] = useState('');
  const [usuarioCorreo, setUsuarioCorreo] = useState('');
  const [usuarioRol, setUsuarioRol] = useState('proveedor');
  const [usuarioCuentaId, setUsuarioCuentaId] = useState('');
  const [claveInicialUsuario, setClaveInicialUsuario] = useState('');
  const [creandoUsuario, setCreandoUsuario] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const generarClaveTemporal = useCallback((length = 10) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }, []);

  const adminActualId = useMemo(() => {
    try {
      const token = getToken();
      if (!token) return null;
      return jwtDecode(token)?.id ?? null;
    } catch {
      return null;
    }
  }, []);

  const verificarRol = useCallback(() => {
    const token = getToken();
    if (!token) {
      navigate('/');
      return false;
    }

    try {
      const decoded = jwtDecode(token);
      if (decoded?.rol !== 'admin') {
        navigate('/');
        return false;
      }
      return true;
    } catch (e) {
      console.error('Error al validar sesion:', e);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
      return false;
    }
  }, [navigate]);

  const fetchUsuarios = useCallback(async () => {
    if (!verificarRol()) return;

    try {
      setLoading(true);
      const response = await api.get('/usuarios', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      const lista = Array.isArray(response.data) ? response.data : [];
      lista.sort((a, b) => String(a?.nombre || '').localeCompare(String(b?.nombre || '')));
      setUsuarios(lista);
    } catch (err) {
      console.error('Error al obtener usuarios:', err);
      if (err?.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/');
        return;
      }
      setError(err.response?.data?.error || 'Error al obtener los usuarios.');
    } finally {
      setLoading(false);
    }
  }, [navigate, verificarRol]);

  const fetchCuentas = useCallback(async () => {
    if (!verificarRol()) return;

    try {
      const response = await api.get('/cuentas', {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      const lista = Array.isArray(response.data) ? response.data : [];
      lista.sort((a, b) => String(a?.nombre || '').localeCompare(String(b?.nombre || '')));
      setCuentas(lista);
    } catch (err) {
      console.error('Error al obtener cuentas:', err);
      setError(err.response?.data?.error || 'Error al obtener las cuentas.');
    }
  }, [verificarRol]);

  const buscarEmpresas = useCallback(async (query, tipo) => {
    if (!verificarRol()) return;

    const q = query.trim();
    if (q.length < 2) {
      setResultadosEmpresa([]);
      setBuscandoEmpresa(false);
      return;
    }

    try {
      setBuscandoEmpresa(true);
      const response = await api.get('/cuentas/catalogo', {
        params: { q, tipo, limit: 8 },
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      setResultadosEmpresa(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error al buscar empresas:', err);
      setResultadosEmpresa([]);
      setError(err.response?.data?.error || 'Error al buscar empresas en SAP.');
    } finally {
      setBuscandoEmpresa(false);
    }
  }, [verificarRol]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  useEffect(() => {
    fetchCuentas();
  }, [fetchCuentas]);

  useEffect(() => {
    setClaveInicialUsuario((prev) => prev || generarClaveTemporal());
  }, [generarClaveTemporal]);

  useEffect(() => {
    setUsuarioCuentaId('');
    setEmpresaSeleccionada(null);
    setResultadosEmpresa([]);
  }, [usuarioRol]);

  useEffect(() => {
    const timer = setTimeout(() => {
      buscarEmpresas(busquedaEmpresa, usuarioRol);
    }, 350);

    return () => clearTimeout(timer);
  }, [buscarEmpresas, busquedaEmpresa, usuarioRol]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleBlanquearCuenta = async (usuario) => {
    const confirmado = window.confirm(
      `Quieres blanquear la cuenta de ${usuario.nombre}?\n\nSe generara una clave temporal y el usuario debera cambiarla al volver a ingresar.`
    );
    if (!confirmado) return;

    try {
      setProcesandoId(usuario.id);
      setMensaje('');
      setError('');

      const response = await api.post(
        `/usuarios/${usuario.id}/reiniciar-clave`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      const claveTemporal = response.data?.claveTemporal || '';
      setClavesTemporales((prev) => ({
        ...prev,
        [usuario.id]: claveTemporal
      }));

      setUsuarios((prev) =>
        prev.map((item) =>
          item.id === usuario.id
            ? { ...item, requiere_cambio_clave: true }
            : item
        )
      );

      setMensaje(`Cuenta blanqueada para ${usuario.nombre}. Comparte la clave temporal mostrada en la tabla.`);
    } catch (err) {
      console.error('Error al blanquear usuario:', err);
      setError(err.response?.data?.error || 'Error al blanquear la cuenta del usuario.');
    } finally {
      setProcesandoId(null);
    }
  };

  const handleCrearCuenta = async (e) => {
    e.preventDefault();

    const nombre = cuentaNombre.trim();
    const sap_id = cuentaSapId.trim();
    if (!nombre) {
      setError('Ingresa un nombre para la empresa.');
      return;
    }

    if (sap_id && sap_id.length > 10) {
      setError('El SAP ID no puede superar los 10 caracteres.');
      return;
    }

    try {
      setCreandoCuenta(true);
      setMensaje('');
      setError('');

      const response = await api.post(
        '/cuentas',
        { nombre, tipo: cuentaTipo, sap_id },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      setCuentaNombre('');
      setCuentaTipo('proveedor');
      setCuentaSapId('');
      setMensaje(`${response.data?.mensaje || 'Empresa creada correctamente.'} Ya puedes asignarla a nuevos usuarios.`);
      await fetchCuentas();
    } catch (err) {
      console.error('Error al crear cuenta:', err);
      setError(err.response?.data?.error || 'Error al crear la empresa.');
    } finally {
      setCreandoCuenta(false);
    }
  };

  const handleSeleccionarEmpresa = async (empresa) => {
    if (!empresa?.nombre || !empresa?.tipo) return;

    try {
      setSeleccionandoEmpresa(true);
      setMensaje('');
      setError('');

      const response = await api.post(
        '/cuentas/seleccionar-catalogo',
        {
          nombre: empresa.nombre,
          tipo: empresa.tipo,
          sap_id: empresa.sap_id || ''
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      const cuenta = response.data?.cuenta;
      if (cuenta?.id) {
        setUsuarioRol(cuenta.tipo);
        setUsuarioCuentaId(String(cuenta.id));
        setEmpresaSeleccionada(cuenta);
        setBusquedaEmpresa(cuenta.nombre);
        if (!usuarioNombre.trim()) {
          setUsuarioNombre(cuenta.nombre);
        }
      }

      setMensaje(`${response.data?.mensaje || 'Empresa seleccionada correctamente.'} Ahora completa correo y clave para crear el usuario asociado.`);
      await fetchCuentas();
    } catch (err) {
      console.error('Error al seleccionar empresa:', err);
      setError(err.response?.data?.error || 'Error al vincular la empresa seleccionada.');
    } finally {
      setSeleccionandoEmpresa(false);
    }
  };

  const handleCrearUsuario = async (e) => {
    e.preventDefault();

    const nombre = usuarioNombre.trim();
    const correo = usuarioCorreo.trim().toLowerCase();
    const clave = claveInicialUsuario.trim();

    if (!nombre) {
      setError('Ingresa el nombre del usuario.');
      return;
    }

    if (!correo) {
      setError('Ingresa el correo del usuario.');
      return;
    }

    if (!usuarioCuentaId) {
      setError('Selecciona una cuenta empresa para el usuario.');
      return;
    }

    if (!clave || clave.length < 6) {
      setError('La clave temporal debe tener al menos 6 caracteres.');
      return;
    }

    try {
      setCreandoUsuario(true);
      setMensaje('');
      setError('');

      const response = await api.post(
        '/usuarios/crear',
        {
          nombre,
          correo,
          clave,
          rol: usuarioRol,
          cuenta_id: Number(usuarioCuentaId)
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      const mensajeBase = response.data?.mensaje || 'Usuario creado exitosamente.';
      const mensajeClave = response.data?.emailEnviado
        ? ` Clave temporal enviada por correo: ${clave}`
        : ` Clave temporal para compartir manualmente: ${clave}`;
      setMensaje(`${mensajeBase}${mensajeClave}`);
      setUsuarioNombre(empresaSeleccionada?.nombre || '');
      setUsuarioCorreo('');
      setClaveInicialUsuario(generarClaveTemporal());
      await fetchUsuarios();
    } catch (err) {
      console.error('Error al crear usuario:', err);
      setError(err.response?.data?.error || 'Error al crear el usuario.');
    } finally {
      setCreandoUsuario(false);
    }
  };

  const handleSeleccionCuenta = (value) => {
    setUsuarioCuentaId(value);

    const cuenta = cuentas.find((item) => String(item.id) === String(value)) || null;
    setEmpresaSeleccionada(cuenta);

    if (cuenta) {
      setBusquedaEmpresa(cuenta.nombre);
      if (!usuarioNombre.trim()) {
        setUsuarioNombre(cuenta.nombre);
      }
    }
  };

  const cuentasFiltradas = useMemo(
    () => cuentas.filter((cuenta) => cuenta?.tipo === usuarioRol),
    [cuentas, usuarioRol]
  );

  const usuariosFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    if (!term) return usuarios;

    return usuarios.filter((usuario) => {
      const cuenta = usuario?.cuenta?.nombre || usuario?.Cuenta?.nombre || '';
      return [usuario?.nombre, usuario?.correo, usuario?.rol, cuenta]
        .some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [usuarios, busqueda]);

  return (
    <div className="page-shell">
      <section className="page-hero">
        <div className="page-hero__content">
          <div>
            <p className="page-hero__eyebrow">Administracion de accesos</p>
            <h2 className="page-hero__title">Usuarios y empresas</h2>
            <p className="page-hero__subtitle">
              Busca empresas desde SAP o local, vincula la seleccion y luego crea el usuario asociado desde un unico panel.
            </p>
          </div>
          <div className="page-hero__meta">
            <span className="hero-chip">{usuarios.length} usuarios</span>
            <span className="hero-chip">{cuentas.length} empresas</span>
          </div>
        </div>
      </section>

      <section className="surface-card">
        <div className="toolbar-stack toolbar-stack--split">
          <div className="toolbar-cluster">
            <button className="btn btn-outline-secondary" onClick={() => navigate('/admin-turnos')}>
              Volver a turnos
            </button>
            <button className="btn btn-outline-primary" onClick={fetchUsuarios} disabled={loading}>
              {loading ? 'Cargando...' : 'Refrescar'}
            </button>
          </div>
          <button className="btn btn-danger" onClick={handleLogout}>
            Cerrar sesion
          </button>
        </div>

        {mensaje && <div className="alert alert-success mt-4 mb-0">{mensaje}</div>}
        {error && <div className="alert alert-danger mt-4 mb-0">{error}</div>}
      </section>

      <section className="dashboard-grid">
        <div style={{ gridColumn: 'span 6' }}>
          <div className="card h-100">
            <div className="card-header">
              <strong>Crear empresa manualmente</strong>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3">
                Usa esta opcion solo si la empresa todavia no aparece en la actualizacion de SAP.
              </p>
              <form onSubmit={handleCrearCuenta}>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label">Nombre de la empresa</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej. Transporte Acme SA"
                      value={cuentaNombre}
                      onChange={(e) => setCuentaNombre(e.target.value)}
                      maxLength={120}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">SAP ID / Solicitante</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej. 10060"
                      value={cuentaSapId}
                      onChange={(e) => setCuentaSapId(e.target.value)}
                      maxLength={10}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Tipo</label>
                    <select
                      className="form-select"
                      value={cuentaTipo}
                      onChange={(e) => setCuentaTipo(e.target.value)}
                    >
                      <option value="proveedor">Proveedor</option>
                      <option value="transportista">Transportista</option>
                    </select>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button type="submit" className="btn btn-primary w-100" disabled={creandoCuenta}>
                      {creandoCuenta ? 'Creando...' : 'Crear'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div style={{ gridColumn: 'span 6' }}>
          <div className="card h-100">
            <div className="card-header">
              <strong>Buscar empresa en SAP / local</strong>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Tipo de empresa</label>
                  <select
                    className="form-select"
                    value={usuarioRol}
                    onChange={(e) => setUsuarioRol(e.target.value)}
                  >
                    <option value="proveedor">Proveedor</option>
                    <option value="transportista">Transportista</option>
                  </select>
                </div>
                <div className="col-md-8">
                  <label className="form-label">Buscar empresa</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Escribe nombre o SAP ID para autocompletar"
                    value={busquedaEmpresa}
                    onChange={(e) => setBusquedaEmpresa(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-3">
                {busquedaEmpresa.trim().length < 2 ? (
                  <p className="text-muted mb-0">
                    Ingresa al menos 2 caracteres para buscar empresas y vincularlas antes de crear el usuario.
                  </p>
                ) : buscandoEmpresa ? (
                  <p className="text-muted mb-0">Buscando empresas...</p>
                ) : resultadosEmpresa.length === 0 ? (
                  <p className="text-muted mb-0">
                    No se encontraron resultados. Si la empresa aun no esta en SAP, puedes crearla manualmente.
                  </p>
                ) : (
                  <div className="list-group">
                    {resultadosEmpresa.map((empresa) => (
                      <button
                        key={`${empresa.tipo}-${empresa.sap_id || empresa.nombre}`}
                        type="button"
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-start"
                        onClick={() => handleSeleccionarEmpresa(empresa)}
                        disabled={seleccionandoEmpresa}
                      >
                        <div className="me-3 text-start">
                          <strong>{empresa.nombre}</strong>
                          <div className="small text-muted">
                            {empresa.sap_id ? `SAP ID ${empresa.sap_id}` : 'Sin SAP ID'}
                          </div>
                        </div>
                        <div className="text-end">
                          <span className={`badge ${empresa.existsLocally ? 'bg-success' : 'bg-primary'}`}>
                            {empresa.existsLocally ? 'Disponible local' : 'Desde SAP'}
                          </span>
                          {empresa.source === 'local+sap' && (
                            <div className="small text-muted mt-1">Sincronizada</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ gridColumn: 'span 12' }}>
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <strong>Crear usuario</strong>
              {empresaSeleccionada && (
                <span className="badge bg-info text-dark">
                  Empresa seleccionada: {empresaSeleccionada.nombre}
                  {empresaSeleccionada.sap_id ? ` - SAP ID ${empresaSeleccionada.sap_id}` : ''}
                </span>
              )}
            </div>
            <div className="card-body">
              <form onSubmit={handleCrearUsuario}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Nombre Empresa</label>
                    <input
                      type="text"
                      className="form-control"
                      value={usuarioNombre}
                      onChange={(e) => setUsuarioNombre(e.target.value)}
                      maxLength={120}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Correo</label>
                    <input
                      type="email"
                      className="form-control"
                      value={usuarioCorreo}
                      onChange={(e) => setUsuarioCorreo(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Rol</label>
                    <select
                      className="form-select"
                      value={usuarioRol}
                      onChange={(e) => setUsuarioRol(e.target.value)}
                    >
                      <option value="proveedor">Proveedor</option>
                      <option value="transportista">Transportista</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cuenta Empresa</label>
                    <select
                      className="form-select"
                      value={usuarioCuentaId}
                      onChange={(e) => handleSeleccionCuenta(e.target.value)}
                      required
                    >
                      <option value="">Selecciona una cuenta...</option>
                      {cuentasFiltradas.map((cuenta) => (
                        <option key={cuenta.id} value={cuenta.id}>
                          {cuenta.nombre}{cuenta.sap_id ? ` - SAP ID ${cuenta.sap_id}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Clave temporal</label>
                    <input
                      type="text"
                      className="form-control"
                      value={claveInicialUsuario}
                      onChange={(e) => setClaveInicialUsuario(e.target.value)}
                      minLength={6}
                      required
                    />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary w-100"
                      onClick={() => setClaveInicialUsuario(generarClaveTemporal())}
                    >
                      Regenerar
                    </button>
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-success" disabled={creandoUsuario}>
                      {creandoUsuario ? 'Creando usuario...' : 'Crear usuario'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="table-card">
        <div className="table-card__header">
          <div>
            <h3 className="table-card__title">Listado de usuarios</h3>
            <p className="table-card__subtitle">
              {usuariosFiltrados.length} resultado(s) segun tu filtro actual.
            </p>
          </div>
          <div style={{ minWidth: '320px' }}>
            <label className="form-label">Buscar usuarios existentes</label>
            <input
              type="text"
              className="form-control"
              placeholder="Nombre, correo, rol o empresa"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="empty-state">
            <p className="empty-state__title">Cargando usuarios</p>
            <p className="empty-state__text">Espera unos segundos mientras se actualiza la lista.</p>
          </div>
        ) : usuariosFiltrados.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state__title">No se encontraron usuarios</p>
            <p className="empty-state__text">Prueba con otro criterio de busqueda o crea un nuevo acceso.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-modern align-middle">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>Rol</th>
                  <th>Empresa</th>
                  <th>Estado de clave</th>
                  <th>Clave temporal</th>
                  <th>Accion</th>
                </tr>
              </thead>
              <tbody>
                {usuariosFiltrados.map((usuario) => {
                  const claveTemporal = clavesTemporales[usuario.id];
                  const cuenta = usuario?.cuenta?.nombre || usuario?.Cuenta?.nombre || 'Sin cuenta';
                  const esUsuarioActual = Number(adminActualId) === Number(usuario.id);

                  return (
                    <tr key={usuario.id}>
                      <td>{usuario.nombre}</td>
                      <td>{usuario.correo}</td>
                      <td className="text-capitalize">{usuario.rol}</td>
                      <td>{cuenta}</td>
                      <td>
                        {usuario.requiere_cambio_clave ? (
                          <span className="badge bg-warning text-dark">Debe cambiar clave</span>
                        ) : (
                          <span className="badge bg-success">Activa</span>
                        )}
                      </td>
                      <td>
                        {claveTemporal ? (
                          <span className="inline-code">{claveTemporal}</span>
                        ) : (
                          <span className="text-muted">Sin blanqueo reciente</span>
                        )}
                      </td>
                      <td>
                        {esUsuarioActual ? (
                          <span className="text-muted">Tu cuenta actual</span>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleBlanquearCuenta(usuario)}
                            disabled={procesandoId === usuario.id}
                          >
                            {procesandoId === usuario.id ? 'Procesando...' : 'Blanquear cuenta'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminUsuarios;
